import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { StatusBadge } from '@/components/hr/status-badge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

// ── Types ─────────────────────────────────────────────────────────────────────

type JobOpening = {
  id: number
  title: string
  departmentId: number | null
  departmentName: string | null
  employmentType: string | null
  positionsCount: number | null
  status: string
  closesAt: string | Date | null
}

type Department = {
  id: number
  name: string
}

// ── Schemas ───────────────────────────────────────────────────────────────────

const createOpeningSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  departmentId: z.number().optional(),
  description: z.string().optional(),
  requirements: z.string().optional(),
  employmentType: z
    .enum(['full_time', 'part_time', 'contract', 'internship'])
    .optional(),
  salaryRangeMin: z.number().nonnegative().optional(),
  salaryRangeMax: z.number().nonnegative().optional(),
  positionsCount: z.number().int().min(1),
  closesAt: z.string().optional(),
})

type CreateOpeningFormValues = z.infer<typeof createOpeningSchema>

export const Route = createFileRoute('/hr/recruitment/openings/')({
  component: OpeningsListPage,
})

function OpeningsListPage() {
  const [statusFilter, setStatusFilter] = useState('all')
  const [departmentFilter, setDepartmentFilter] = useState('all')
  const [isFormOpen, setIsFormOpen] = useState(false)

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: openingsData, isLoading } = useQuery(
    orpc.hr.listJobOpenings.queryOptions({
      input: {
        page: 1,
        limit: 100,
        status:
          statusFilter === 'all'
            ? undefined
            : (statusFilter as 'draft' | 'open' | 'on_hold' | 'closed' | 'filled'),
        departmentId:
          departmentFilter === 'all' ? undefined : Number(departmentFilter),
      },
    }),
  )

  const { data: departmentsData } = useQuery(
    orpc.hr.getDepartments.queryOptions({ input: {} }),
  )

  const openings = (openingsData?.data ?? []) as JobOpening[]
  const departments = (departmentsData ?? []) as Department[]

  const createMutation = useMutation(
    orpc.hr.createJobOpening.mutationOptions({
      onSuccess: () => {
        toast.success('Job opening created')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listJobOpenings']] })
        setIsFormOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Job Openings</h1>
          <p className="text-muted-foreground">Manage open positions and track applicants</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Opening
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="filled">Filled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id.toString()}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : openings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No job openings found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Positions</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Closes At</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {openings.map((opening) => (
                  <TableRow
                    key={opening.id}
                    className="cursor-pointer"
                    onClick={() => void navigate({ to: `/hr/recruitment/openings/${opening.id}` })}
                  >
                    <TableCell className="font-medium">{opening.title}</TableCell>
                    <TableCell>{opening.departmentName ?? '-'}</TableCell>
                    <TableCell>{opening.positionsCount ?? 1}</TableCell>
                    <TableCell>
                      {opening.employmentType ? (
                        <Badge variant="outline" className="capitalize">
                          {opening.employmentType.replace('_', ' ')}
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {opening.closesAt
                        ? new Date(opening.closesAt).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={opening.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateOpeningDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        departments={departments}
        onSubmit={async (data) => {
          await createMutation.mutateAsync({
            ...data,
            closesAt: data.closesAt ? new Date(data.closesAt) : undefined,
          })
        }}
      />
    </div>
  )
}

// ── Create Opening Dialog ─────────────────────────────────────────────────────

interface CreateOpeningDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  departments: Department[]
  onSubmit: (data: CreateOpeningFormValues) => Promise<void>
}

function CreateOpeningDialog({
  open,
  onOpenChange,
  departments,
  onSubmit,
}: CreateOpeningDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<CreateOpeningFormValues>({
    resolver: zodResolver(createOpeningSchema),
    defaultValues: {
      title: '',
      positionsCount: 1,
    },
  })

  const handleSubmit = async (data: CreateOpeningFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Job Opening</DialogTitle>
          <DialogDescription>Create a new position to recruit for</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input placeholder="Senior Engineer" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="departmentId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Department</FormLabel>
                  <Select
                    onValueChange={(v) =>
                      field.onChange(v === 'none' ? undefined : Number(v))
                    }
                    value={field.value?.toString() ?? 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No department</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id.toString()}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="employmentType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employment Type</FormLabel>
                  <Select
                    onValueChange={(v) =>
                      field.onChange(
                        v === 'none'
                          ? undefined
                          : (v as 'full_time' | 'part_time' | 'contract' | 'internship'),
                      )
                    }
                    value={field.value ?? 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="internship">Internship</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="positionsCount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Positions</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="salaryRangeMin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Salary Min</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="40000"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : undefined,
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="salaryRangeMax"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Salary Max</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="80000"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : undefined,
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="closesAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Closes At</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Job description..."
                      rows={3}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="requirements"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Requirements</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Job requirements..."
                      rows={3}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Opening'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
